import React, { useState } from "react";
import { Box, Typography, Button, IconButton, FormControl, Select, MenuItem, TextField, Collapse, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SavingsIcon from "@mui/icons-material/Savings";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import dayjs from "dayjs";
import {
  useGetTariffListQuery,
  useAddTariffMutation,
  useUpdateBulkTariffMutation,
  useDeleteTariffMutation,
} from "../api/financialApiSlice";
import CSVBulkUpdate from "../UserSettings/CSVBulkUpdate";

const LABEL_SX = {
  fontSize: "0.58rem", fontWeight: 600, letterSpacing: "0.08em",
  textTransform: "uppercase", color: "rgba(255,255,255,0.3)", mb: "5px",
};

const INFO_TOOLTIP_SX = {
  tooltip: {
    sx: {
      bgcolor: "#1C232C", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px",
      p: "12px 14px", maxWidth: 300, boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    },
  },
  arrow: { sx: { color: "#1C232C" } },
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#0d1117", borderRadius: "6px", fontSize: "0.84rem", color: "#e0e6f0",
    "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.22)" },
    "&.Mui-focused fieldset": { borderColor: "#FDB940 !important", borderWidth: "1px !important" },
  },
  "& .MuiInputBase-input": { padding: "7px 10px" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.4)" },
};

const MENU_PROPS = {
  PaperProps: {
    sx: {
      backgroundColor: "#1C232C", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px",
      "& .MuiMenuItem-root": {
        fontSize: "0.84rem", color: "#e0e6f0",
        "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
        "&.Mui-selected": { bgcolor: "rgba(253,185,64,0.1)" },
      },
    },
  },
};

const TYPE_COLORS = {
  Peak:     { bg: "rgba(253,185,64,0.15)",  color: "#FDB940", border: "rgba(253,185,64,0.35)"  },
  Standard: { bg: "rgba(74,222,128,0.1)",   color: "#4ade80", border: "rgba(74,222,128,0.3)"   },
  OffPeak:  { bg: "rgba(147,197,253,0.1)",  color: "#93C5FD", border: "rgba(147,197,253,0.3)"  },
  Demand:   { bg: "rgba(251,146,60,0.1)",   color: "#fb923c", border: "rgba(251,146,60,0.3)"   },
};

function TypeChip({ value }) {
  const c = TYPE_COLORS[value] || { bg: "rgba(255,255,255,0.08)", color: "#e0e6f0", border: "rgba(255,255,255,0.15)" };
  return (
    <Box sx={{ bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: "6px", px: "8px", py: "3px", fontSize: "0.75rem", fontWeight: 600, display: "inline-block" }}>
      {value}
    </Box>
  );
}

export default function Tariff() {
  const { data: tariff_data, isSuccess, refetch } = useGetTariffListQuery();
  const [addTariff, { isSuccess: isAddSuccess }] = useAddTariffMutation();
  const [updateBulkTariff, { isSuccess: isUpdateSuccess, isError: isUpdateError }] = useUpdateBulkTariffMutation();

  const [bulkArray, setBulkArray] = useState([]);
  const [saved, setSaved] = useState("Saved!");
  const [deletingId, setDeletingId] = useState(null);
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  React.useEffect(() => { if (isSuccess) setBulkArray(tariff_data); }, [isSuccess, tariff_data]);
  React.useEffect(() => { if (isAddSuccess) refetch(); }, [refetch, isAddSuccess]);
  React.useEffect(() => {
    if (isUpdateSuccess) setSaved("Saved!");
    else if (isUpdateError) setSaved("Save");
  }, [isUpdateSuccess, isUpdateError]);

  const handleAdd = () => {
    addTariff({ body: { type: "Peak", start_date: "", end_date: "", rate: 0 } });
  };

  if (!isSuccess) return null;

  return (
    <Box sx={{ backgroundColor: "#1C232C", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "12px", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", px: "16px", py: "12px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <SavingsIcon sx={{ fontSize: "1rem", color: "#FDB940", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#fff" }}>Tariff</Typography>
        <Tooltip
          title={
            <Box>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#fff", mb: "4px" }}>Tariff rates</Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>
                Rates per kWh for energy and kW for demand. Note that for energy, the rates are the values that will be used to determine the effective differentials between Peak and Standard and between Standard and Off-Peak.
              </Typography>
            </Box>
          }
          placement="bottom"
          arrow
          componentsProps={INFO_TOOLTIP_SX}
        >
          <InfoOutlinedIcon sx={{ fontSize: "14px", color: "rgba(255,255,255,0.3)", cursor: "help", transition: "color 0.18s", "&:hover": { color: "#FDB940" } }} />
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={handleAdd} sx={{ color: "rgba(255,255,255,0.45)", "&:hover": { color: "#FDB940" } }}>
          <AddIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
        <IconButton size="small" onClick={() => setCsvModalOpen(true)} sx={{ color: "rgba(255,255,255,0.45)", "&:hover": { color: "#FDB940" } }}>
          <UploadFileIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      {/* Rows */}
      <Box sx={{ p: "12px", flexGrow: 1 }}>
        {tariff_data?.map((tariff) => (
          <TariffRow
            key={tariff.id}
            tariff={tariff}
            bulkArray={bulkArray}
            setBulkArray={setBulkArray}
            setSaved={setSaved}
            deletingId={deletingId}
            setDeletingId={setDeletingId}
          />
        ))}
        {tariff_data?.length === 0 && (
          <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.3)", textAlign: "center", py: "16px" }}>
            No tariff entries. Click + to add one.
          </Typography>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ px: "16px", py: "10px", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          disabled={saved !== "Save"}
          onClick={() => updateBulkTariff({ body: bulkArray })}
          sx={{
            bgcolor: "#FDB940", color: "#0d1117", fontWeight: 600, fontSize: "0.78rem",
            textTransform: "none", borderRadius: "8px", boxShadow: "none", px: "16px", py: "5px",
            "&:hover": { bgcolor: "#e8a830", boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: "rgba(253,185,64,0.25)", color: "rgba(0,0,0,0.4)" },
          }}
        >
          {saved}
        </Button>
      </Box>

      <CSVBulkUpdate
        open={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        csvType="tariff-data"
        onSuccess={() => { refetch(); setSaved("Save"); }}
      />
    </Box>
  );
}

function TariffRow({ tariff, bulkArray, setBulkArray, setSaved, deletingId, setDeletingId }) {
  const [deleteTariff] = useDeleteTariffMutation();
  const [type, setType] = useState(tariff.type);
  const [rate, setRate] = useState(tariff.rate);
  const [startDate, setStartDate] = useState(dayjs(tariff.start_date));
  const [endDate, setEndDate] = useState(dayjs(tariff.end_date));

  const update = (field, value) => {
    setBulkArray((prev) => prev.map((item) => item.id === tariff.id ? { ...item, [field]: value } : item));
    setSaved("Save");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ mb: "6px" }}>
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "12px", px: "14px", py: "10px", backgroundColor: "#161B22", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "8px" }}>

          {/* Type */}
          <Box sx={{ flex: 1.2, minWidth: 0 }}>
            <Typography sx={LABEL_SX}>Type</Typography>
            <Select
              value={type}
              onChange={(e) => { setType(e.target.value); update("type", e.target.value); }}
              size="small"
              fullWidth
              renderValue={(val) => <TypeChip value={val} />}
              sx={{ bgcolor: "transparent", "& .MuiOutlinedInput-notchedOutline": { border: "none" }, "& .MuiSelect-select": { padding: "4px 20px 4px 0 !important" }, "& .MuiSelect-icon": { color: "rgba(255,255,255,0.3)", right: 0 } }}
              MenuProps={MENU_PROPS}
            >
              {["Peak", "Standard", "OffPeak", "Demand"].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </Box>

          {/* Rate */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={LABEL_SX}>Rate (R)</Typography>
            <TextField
              value={rate}
              size="small"
              onChange={(e) => { setRate(e.target.value); update("rate", e.target.value); }}
              sx={INPUT_SX}
            />
          </Box>

          {/* From */}
          <Box sx={{ flex: 1.8, minWidth: 0 }}>
            <Typography sx={LABEL_SX}>From</Typography>
            <MobileDatePicker
              format="YYYY/MM/DD"
              value={startDate}
              onChange={(date) => {
                const d = date.set("hour", 0).set("minute", 0).set("second", 0);
                setStartDate(d);
                update("start_date", d.format("YYYY-MM-DD HH:mm:ss"));
              }}
              slotProps={{ textField: { size: "small", fullWidth: true, sx: INPUT_SX } }}
            />
          </Box>

          {/* To */}
          <Box sx={{ flex: 1.8, minWidth: 0 }}>
            <Typography sx={LABEL_SX}>To</Typography>
            <MobileDatePicker
              format="YYYY/MM/DD"
              value={endDate}
              onChange={(date) => {
                const d = date.set("hour", 21).set("minute", 59).set("second", 59);
                setEndDate(d);
                update("end_date", d.format("YYYY-MM-DD HH:mm:ss"));
              }}
              slotProps={{ textField: { size: "small", fullWidth: true, sx: INPUT_SX } }}
            />
          </Box>

          <IconButton size="small" onClick={() => setDeletingId(tariff.id)}
            sx={{ color: "rgba(255,255,255,0.3)", "&:hover": { color: "#f87171", bgcolor: "rgba(248,113,113,0.08)" }, flexShrink: 0 }}>
            <DeleteIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Collapse in={deletingId === tariff.id}>
          <Box sx={{ mt: "3px", px: "12px", py: "8px", backgroundColor: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.6)" }}>Delete this tariff entry?</Typography>
            <Box sx={{ display: "flex", gap: "6px" }}>
              <Button size="small" onClick={() => setDeletingId(null)} sx={{ fontSize: "0.75rem", textTransform: "none", color: "rgba(255,255,255,0.5)" }}>Cancel</Button>
              <Button size="small" onClick={() => { deleteTariff({ tariff_id: tariff.id }); setDeletingId(null); }}
                sx={{ fontSize: "0.75rem", textTransform: "none", color: "#f87171", border: "1px solid rgba(248,113,113,0.35)", borderRadius: "5px", px: "8px", "&:hover": { bgcolor: "rgba(248,113,113,0.1)" } }}>
                Delete
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </LocalizationProvider>
  );
}
